import React from 'react';
import { Link } from 'react-router-dom';
import { RiErrorWarningLine, RiHomeLine } from 'react-icons/ri';
import { useAuth } from '../contexts/AuthContext';
import { usePermission } from '../hooks/usePermission';
import { resolveLandingRoute } from '../config/navigation';
import { buildMenuAccessChecker } from '../utils/permissions';

const NotFoundPage: React.FC = () => {
  // G-15: ปุ่มหน้าหลักชี้เมนูแรกที่ role นี้เข้าได้จริง (แบบเดียวกับ AccessDenied)
  // หน้านี้อาจ render ตอน auth ยังไม่พร้อม — useAuth คืน safe defaults (ไม่ throw)
  // และเมื่อยังไม่ authenticated ให้คง /dashboard ตามเดิม
  const { isAuthenticated, permissions } = useAuth();
  const { isSuperAdmin, isSuperUser } = usePermission();
  const homeTarget = isAuthenticated
    ? resolveLandingRoute(buildMenuAccessChecker(permissions, isSuperAdmin || isSuperUser))
    : '/dashboard';

  return (
    <main aria-label="หน้าไม่พบ" className="min-h-screen relative overflow-hidden bg-gradient-to-br from-primary-600 via-primary-700 to-primary-800 flex flex-col justify-center items-center p-4">{/* FINDING-043 FIX (S410) — wrap 404 page content in <main> landmark */}
      {/* Decorative Elements */}
      <div className="absolute inset-0 bg-grid-white/5" />
      <div className="absolute -top-24 -right-24 w-96 h-96 bg-white/10 rounded-full blur-3xl" />
      <div className="absolute -bottom-32 -left-32 w-80 h-80 bg-primary-400/20 rounded-full blur-3xl" />
      <div className="absolute top-1/3 right-1/4 w-64 h-64 bg-white/5 rounded-full blur-2xl" />

      <div className="relative max-w-md w-full">
        {/* 404 Icon */}
        <div className="flex justify-center mb-8">
          <div className="w-32 h-32 bg-white/10 backdrop-blur-sm rounded-full flex items-center justify-center border-4 border-white/20 shadow-2xl">
            <RiErrorWarningLine className="text-white h-16 w-16" />
          </div>
        </div>

        {/* Content Card */}
        <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-2xl overflow-hidden border border-white/20">
          <div className="p-8">
            <h1 className="text-6xl font-bold text-center bg-gradient-to-r from-primary-600 to-primary-800 bg-clip-text text-transparent mb-3">404</h1>
            <h2 className="text-2xl font-semibold text-center text-secondary-800 mb-4">ไม่พบหน้าที่คุณต้องการ</h2>

            <p className="text-secondary-600 text-center mb-8 leading-relaxed">
              หน้าที่คุณกำลังค้นหาอาจถูกลบ ย้าย หรือไม่มีอยู่ในระบบ
            </p>

            <div className="flex flex-col space-y-3">
              <Link
                to={homeTarget}
                className="group flex items-center justify-center px-6 py-3 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-all"
              >
                <RiHomeLine className="mr-2 h-5 w-5 group-hover:scale-110 transition-transform duration-300" />
                กลับไปยังหน้าหลัก
              </Link>

              <button
                onClick={() => window.history.back()}
                className="flex items-center justify-center px-6 py-3 border border-secondary-300 rounded-md shadow-sm text-sm font-medium text-secondary-700 bg-white hover:bg-secondary-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-all"
              >
                กลับไปยังหน้าก่อนหน้า
              </button>
            </div>
          </div>
        </div>

        <div className="mt-8 text-center text-sm text-white/90 backdrop-blur-sm bg-white/5 rounded-xl px-6 py-3 border border-white/10">
          หากคุณเชื่อว่านี่เป็นข้อผิดพลาดของระบบ โปรดติดต่อผู้ดูแลระบบ
        </div>
      </div>
    </main>
  );
};

export default NotFoundPage;
